import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useAuth } from "./authProvider";
import adminNotificationsService from "../../services/adminNotificationsService";

const UNREAD_POLL_INTERVAL_SEC = 60;

const notificationContext = createContext({
  unreadCount: 0,
  refreshUnread: async () => {},
});

export function AdminNotificationUnreadProvider({ children }) {
  const { user, isAdmin } = useAuth() || {};
  const [unreadCount, setUnreadCount] = useState(0);
  const pollingTimerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!user || !isAdmin) {
      setUnreadCount(0);
      return;
    }
    try {
      const res = await adminNotificationsService.list({
        page: 0,
        size: 1,
        state: "not-read",
      });
      setUnreadCount(Number(res.totalCount) || 0);
    } catch (e) {
      console.warn("admin notification unread poll failed", e);
      setUnreadCount(0);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    stopPolling();
    if (!user || !isAdmin) {
      setUnreadCount(0);
      return undefined;
    }
    refreshUnread();
    const ms = Math.max(1, UNREAD_POLL_INTERVAL_SEC) * 1000;
    pollingTimerRef.current = setInterval(refreshUnread, ms);
    return stopPolling;
  }, [user, isAdmin, refreshUnread, stopPolling]);

  const value = useMemo(
    () => ({ unreadCount, refreshUnread }),
    [unreadCount, refreshUnread]
  );

  return (
    <notificationContext.Provider value={value}>
      {children}
    </notificationContext.Provider>
  );
}

export function useAdminNotificationUnread() {
  return useContext(notificationContext);
}

AdminNotificationUnreadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
 